import React, { useState } from 'react'
import { withStyles } from '@mui/styles'
import {
  TextField,
  FormControl,
  FormHelperText,
  InputAdornment,
  IconButton,
  Icon,
  Tooltip,
  Typography,
} from '@mui/material'

import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

import { passwordInputStyles } from './password-input.styles'

/**
 * @typedef {object} PasswordInputChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {string} target.value - El nuevo valor de la contraseña.
 */

/**
 * @typedef {object} PasswordInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {boolean} [required=false] - Indica si el campo es obligatorio.
 * @property {string} [value] - El valor actual del campo de contraseña.
 * @property {string} [title] - El título o etiqueta que se muestra sobre el campo.
 * @property {string} [name] - El nombre del campo, que se incluirá en el objeto de evento del onChange.
 * @property {string} [placeholder] - Texto de marcador de posición que aparece cuando el campo está vacío.
 * @property {string} [tooltip] - Texto informativo que se muestra en un icono de ayuda junto al título.
 * @property {string} [helpMessage] - Mensaje de ayuda que se muestra debajo del campo.
 * @property {boolean} [error] - Si es true, el campo se muestra en estado de error.
 * @property {string} [errorMessage] - Mensaje de error que se muestra cuando error es true.
 * @property {object} [InputProps] - Props adicionales para el componente TextField de Material-UI.
 * @property {function(PasswordInputChangeEvent): void} onChange - La función callback que se ejecuta cuando cambia el valor.
 */

/**
 * Un componente de entrada de contraseña con funcionalidad de mostrar/ocultar.
 *
 * Proporciona un campo de texto específico para contraseñas con un botón toggle
 * para alternar la visibilidad del texto ingresado. Incluye soporte para mensajes
 * de error, ayuda, y tooltips informativos.
 *
 * @component
 * @example
 * const MyLoginForm = () => {
 *   const [password, setPassword] = useState('');
 *   const [error, setError] = useState(false);
 *
 *   const handleChange = (e) => {
 *     console.log(`El campo '${e.target.name}' tiene ${e.target.value.length} caracteres`);
 *     setPassword(e.target.value);
 *     setError(e.target.value.length < 8);
 *   };
 *
 *   return (
 *     <PasswordInput
 *       title="Contraseña"
 *       name="password"
 *       value={password}
 *       onChange={handleChange}
 *       required
 *       placeholder="Ingrese su contraseña"
 *       error={error}
 *       errorMessage="La contraseña debe tener al menos 8 caracteres"
 *       tooltip="Use mayúsculas, minúsculas y números"
 *     />
 *   );
 * };
 *
 * @param {PasswordInputProps} props - Las props del componente PasswordInput.
 */
export const PasswordInput = withStyles(passwordInputStyles)(({ classes, ...props }) => {
  const [visibility, setVisibility] = useState(false)

  return (
    <FormControl className={classes.root}>
      <div className={classes.title}>
        {props.title}
        {props.tooltip && (
          <span style={{ fontSize: 16, marginLeft: 4 }}>
            <Tooltip
              placement="top"
              title={
                <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                  {props.tooltip}
                </Typography>
              }
            >
              <Icon fontSize="inherit">help_outline</Icon>
            </Tooltip>
          </span>
        )}
      </div>
      <TextField
        {...props}
        type={visibility ? 'text' : 'password'}
        variant="outlined"
        value={props.value || ''}
        placeholder={props.placeholder}
        onChange={props.onChange}
        InputProps={{
          ...props.InputProps,
          className: classes.input,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setVisibility(!visibility)}>
                {visibility ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <FormHelperText id="my-helper-text" error={props.error}>
        {props.error ? props.errorMessage : props.helpMessage}
      </FormHelperText>
    </FormControl>
  )
})
